import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LegacyGenreResponse } from '../types';
import { gameService } from '../services/api';
import GameList from '../components/GameList';
import Pagination from '../components/Pagination';
import { GameListSkeleton } from '../components/skeletons';

const HomePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [featuredData, setFeaturedData] = useState<LegacyGenreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentPage = parseInt(searchParams.get('page') || '1');

  // Préchargement des données
  const fetchFeaturedGames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Pré-chargement immédiat avec données en cache si disponibles
      const cachedData = sessionStorage.getItem(`featured_${currentPage}`);
      if (cachedData) {
        setFeaturedData(JSON.parse(cachedData));
      }

      const data = await gameService.getFeaturedGames({ page: currentPage });
      setFeaturedData(data);
      
      // Cache les données pour les visites futures
      sessionStorage.setItem(`featured_${currentPage}`, JSON.stringify(data));
    } catch (err) {
      console.error('Error fetching featured games:', err);
      setError('Erreur lors du chargement des jeux');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchFeaturedGames();
  }, [fetchFeaturedGames]);

  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDiscordClick = () => {
    window.open('https://discord.gg/CC6utFVjBn', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section - Premium Style */}
      <section className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-blue-600/10 to-cyan-500/10 animate-pulse"></div>
          <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-sky-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-24">
          <div className="text-center">
            {/* Main Title with Glow Effect */}
            <div className="mb-8">
              <h1 className="text-6xl md:text-8xl font-black bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent mb-4 tracking-tight">
                TuCracks
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto mb-6 rounded-full"></div>
            </div>

            {/* Tagline */}
            <div className="mb-12">
              <p className="text-xl md:text-2xl text-gray-300 font-light mb-6">
                La plateforme ultime de jeux <span className="text-red-400 font-bold">GRATUITS</span>
              </p>
              
              {/* Stats Bar */}
              <div className="flex justify-center items-center space-x-8 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>100% Sans Publicité</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span>Téléchargement Direct</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                  <span>Mise à Jour Quotidienne</span>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => document.getElementById('featured')?.scrollIntoView({ behavior: 'smooth' })}
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl hover:from-blue-500 hover:to-cyan-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg overflow-hidden"
              >
                <span className="relative z-10">Explorer les Jeux</span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>

              {/* Bouton Discord */}
              <button 
                onClick={handleDiscordClick}
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl hover:from-blue-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg overflow-hidden border-2 border-blue-400/30"
              >
                <svg 
                  className="w-6 h-6 mr-3 relative z-10" 
                  fill="currentColor" 
                  viewBox="0 0 127.14 96.36"
                >
                  <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                </svg>
                <span className="relative z-10">Rejoindre Discord</span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </div>

            {/* Discord Info */}
            <div className="mt-6">
              <p className="text-gray-400 text-sm">
                Rejoignez notre communauté Discord pour du support, des annonces et plus encore !
              </p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Featured Games Section */}
      <section id="featured" className="relative py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block relative">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Jeux du Moment
              </h2>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full opacity-60"></div>
            </div>
            <p className="text-xl text-gray-300 mt-8 max-w-3xl mx-auto">
              Découvrez notre sélection premium des jeux les plus populaires. 
              Téléchargement rapide, installation simplifiée.
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white/5 rounded-2xl p-6 border border-white/10 backdrop-blur-sm">
                  <div className="animate-pulse">
                    <div className="bg-gray-700 rounded-lg h-48 mb-4"></div>
                    <div className="bg-gray-700 rounded h-4 mb-2"></div>
                    <div className="bg-gray-700 rounded h-3 w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredData ? (
            <>
              <GameList games={featuredData.games} error={error} />
              
              {featuredData.total_pages > 1 && (
                <div className="mt-16">
                  <Pagination
                    currentPage={featuredData.current_page}
                    totalPages={featuredData.total_pages}
                    hasNext={featuredData.has_next}
                    hasPrevious={featuredData.has_previous}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="bg-white/5 rounded-2xl p-12 border border-white/10 backdrop-blur-sm max-w-md mx-auto">
                <div className="text-6xl mb-4">🎮</div>
                <div className="text-white text-lg mb-2">Aucun jeu disponible</div>
                <div className="text-gray-400">Revenez plus tard pour découvrir de nouveaux jeux</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Discord CTA Section */}
      <section className="relative py-16 bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-t border-blue-500/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center">
                <svg 
                  className="w-10 h-10 text-white" 
                  fill="currentColor" 
                  viewBox="0 0 127.14 96.36"
                >
                  <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                </svg>
              </div>
            </div>
            
            <h3 className="text-3xl font-bold text-white mb-4">
              Rejoignez Notre Communauté
            </h3>
            
            <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
              Obtenez de l'aide, restez informé des dernières sorties et échangez avec d'autres passionnés de jeux vidéo.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-2xl mx-auto">
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-white font-bold">💬</span>
                </div>
                <p className="text-white font-semibold">Support Rapide</p>
              </div>
              
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-white font-bold">🚀</span>
                </div>
                <p className="text-white font-semibold">Annonces Exclusives</p>
              </div>
              
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-white font-bold">👥</span>
                </div>
                <p className="text-white font-semibold">Communauté Active</p>
              </div>
            </div>

            <button 
              onClick={handleDiscordClick}
              className="group inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl hover:from-blue-400 hover:to-cyan-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg overflow-hidden border-2 border-blue-400/30"
            >
              <svg 
                className="w-6 h-6 mr-3 group-hover:animate-pulse" 
                fill="currentColor" 
                viewBox="0 0 127.14 96.36"
              >
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
              </svg>
              <span>Rejoindre le Discord</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;